import { useMemo } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { EUserRoleName, PartnerBaseRoute } from "constants/index";

const ALL_ROLES = [
  EUserRoleName[EUserRoleName.gc_project_admin],
  EUserRoleName[EUserRoleName.gc_foreman],
  EUserRoleName[EUserRoleName.gc_sc_project_admin],
  EUserRoleName[EUserRoleName.gc_sc_foreman],
  EUserRoleName[EUserRoleName.gc_sc_architect],
  EUserRoleName[EUserRoleName.gc_sc_design_engineer],
  EUserRoleName[EUserRoleName.gc_sc_owner]
];

const GC_PROJECT_ADMIN = [EUserRoleName[EUserRoleName.gc_project_admin]];

const GC_PROJECT_ADMIN_FOREMAN = [
  EUserRoleName[EUserRoleName.gc_project_admin],
  EUserRoleName[EUserRoleName.gc_foreman]
];

const PAGE_ROLE_MAPPING: Record<string, string[]> = {
  dashboard: ALL_ROLES,
  "bid-packages": GC_PROJECT_ADMIN_FOREMAN,
  "bid-packages/:bidPackageId": GC_PROJECT_ADMIN_FOREMAN,
  "design-packages": GC_PROJECT_ADMIN_FOREMAN,
  "design-packages/:designPackageId": GC_PROJECT_ADMIN_FOREMAN,
  settings: ALL_ROLES,
  "settings/modules": GC_PROJECT_ADMIN_FOREMAN,
  "settings/locations": GC_PROJECT_ADMIN_FOREMAN,
  "settings/integrations": GC_PROJECT_ADMIN_FOREMAN,
  "settings/general": ALL_ROLES,
  "settings/general/project-users": ALL_ROLES,
  "settings/general/project-companies": GC_PROJECT_ADMIN_FOREMAN,
  "settings/general/details": GC_PROJECT_ADMIN_FOREMAN,
  "settings/general/calendar": GC_PROJECT_ADMIN_FOREMAN,
  "settings/general/distribution-groups": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-packages": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-packages/rename-fields": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/design-packages/templates": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/bid-packages": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/bid-packages/rename-fields": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/bid-packages/risk-threshold": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/bid-packages/templates": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/actions": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/rename-field": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/risk-threshold": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/numbering": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/submittals/dateblock": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/materials": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/materials/rename-field": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/materials/risk-threshold": GC_PROJECT_ADMIN_FOREMAN,
  "settings/modules/materials/templates": GC_PROJECT_ADMIN_FOREMAN,
  Submittals: ALL_ROLES,
  submittals: ALL_ROLES,
  "submittals/:submittalId": ALL_ROLES,
  Materials: ALL_ROLES,
  materials: ALL_ROLES,
  "materials/:materialId": ALL_ROLES,
  "materials/extracted": GC_PROJECT_ADMIN_FOREMAN,
  schedule: ALL_ROLES,
  "schedule/submittal-linking": GC_PROJECT_ADMIN_FOREMAN,
  "schedule/material-linking": GC_PROJECT_ADMIN_FOREMAN,
  "schedule/change-impact": GC_PROJECT_ADMIN,
  "schedule/procurement-schedule": ALL_ROLES,
  "schedule/project-schedules": ALL_ROLES,
  "project-schedule/:selectedVersionId": ALL_ROLES,
  reports: GC_PROJECT_ADMIN_FOREMAN,
  "reports/risk": GC_PROJECT_ADMIN_FOREMAN,
  "reports/upcoming": GC_PROJECT_ADMIN_FOREMAN,
  "reports/activities": GC_PROJECT_ADMIN_FOREMAN,
  "custom-report/:reportId": GC_PROJECT_ADMIN_FOREMAN,
  "reports/procurement-follow-ups": GC_PROJECT_ADMIN_FOREMAN
};

const VALID_ROUTE_PATTERNS = [
  // Public routes
  { path: "/", exact: true },
  { path: "/Login", exact: true },
  { path: "/callback", exact: true },
  { path: "/subscription-select", exact: true },
  { path: "/set_password", exact: true },
  { path: "/reset_password", exact: true },
  { path: "/forgot_password", exact: true },
  { path: "/reset_expired_password", exact: true },
  { path: "/accept-invite", exact: true },
  { path: "/io/:projectId", exact: true },
  { path: "/submit-lead-time", exact: true },
  { path: "/admin", exact: true },
  { path: PartnerBaseRoute, exact: false },

  // Private routes - Home level
  { path: "/home", exact: true },
  { path: "/new-project", exact: true },
  { path: "/change_password", exact: true },
  { path: "/users", exact: true },
  { path: "/companies", exact: true },
  { path: "/projects", exact: true },
  { path: "/analytics", exact: true },
  { path: "/settings", exact: true },
  { path: "/settings/:tab1Id", exact: true },
  { path: "/settings/:tab1Id/:tab2Id", exact: true },
  { path: "/settings/:tab1Id/:tab2Id/:tab3Id", exact: true },

  // Private routes - Project level
  { path: "/project/:projectId/dashboard", exact: true },
  { path: "/project/:projectId/design", exact: true },
  { path: "/project/:projectId/submittals", exact: true },
  { path: "/project/:projectId/submittals/:submittalId", exact: true },
  { path: "/project/:projectId/Submittals/:submittalId", exact: true },
  { path: "/project/:projectId/bid-packages", exact: true },
  { path: "/project/:projectId/bid-packages/:bidPackageId", exact: true },
  { path: "/project/:projectId/design-packages", exact: true },
  { path: "/project/:projectId/design-packages/:designPackageId", exact: true },
  { path: "/project/:projectId/settings", exact: true },
  { path: "/project/:projectId/settings/:tab1Id", exact: true },
  { path: "/project/:projectId/settings/:tab1Id/:tab2Id", exact: true },
  { path: "/project/:projectId/settings/:tab1Id/:tab2Id/:tab3Id", exact: true },
  { path: "/project/:projectId/materials", exact: true },
  { path: "/project/:projectId/materials/extracted", exact: true },
  { path: "/project/:projectId/materials/:materialId", exact: true },
  { path: "/project/:projectId/Materials/:materialId", exact: true },
  { path: "/project/:projectId/schedule", exact: true },
  { path: "/project/:projectId/schedule/project-schedules", exact: true },
  {
    path: "/project/:projectId/schedule/project-schedule/:selectedVersionId",
    exact: true
  },
  { path: "/project/:projectId/reports", exact: true },
  { path: "/project/:projectId/reports/risk", exact: true },
  { path: "/project/:projectId/reports/upcoming", exact: true },
  { path: "/project/:projectId/reports/activities", exact: true },
  { path: "/project/:projectId/reports/procurement-follow-ups", exact: true },
  { path: "/project/:projectId/reports/custom-report/:reportId", exact: true },
  { path: "/project/:projectId/reports/submittals/export", exact: true },
  { path: "/project/:projectId/schedule/change-impact", exact: true },
  { path: "/project/:projectId/schedule/submittal-linking", exact: true },
  { path: "/project/:projectId/schedule/material-linking", exact: true }
];

/**
 * Details page patterns that should bypass role checking
 * These are detail pages that inherit permissions from their parent pages
 */
const DETAILS_PAGE_PATTERNS = [
  /\/project\/[^/]+\/submittals\/[^/]+$/,
  /\/project\/[^/]+\/bid-packages\/[^/]+$/,
  /\/project\/[^/]+\/design-packages\/[^/]+$/,
  /\/project\/[^/]+\/materials\/[^/]+$/,
  /\/project\/[^/]+\/schedule\/project-schedule\/[^/]+$/,
  /\/project\/[^/]+\/reports\/custom-report\/[^/]+$/
];

function extractPageKey(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);

  // Handle project routes
  if (parts[0] === "project" && parts.length >= 3) {
    const pageName = parts[2];
    const subPage = parts[3];
    const fourthPart = parts[4];
    const fifthPart = parts[5];

    const isDetails = DETAILS_PAGE_PATTERNS.some((pattern) =>
      pattern.test(pathname)
    );

    // Handle bid-packages
    if (pageName === "bid-packages") {
      if (isDetails && subPage && !fourthPart) {
        return "bid-packages/:bidPackageId";
      }
      return "bid-packages";
    }

    // Handle design-packages
    if (pageName === "design-packages") {
      if (isDetails && subPage && !fourthPart) {
        return "design-packages/:designPackageId";
      }
      return "design-packages";
    }

    // Handle submittals
    if (pageName === "submittals") {
      if (isDetails && subPage && !fourthPart) {
        return "submittals/:submittalId";
      }
      return "submittals";
    }

    // Handle materials/extracted
    if (pageName === "materials") {
      if (subPage === "extracted") {
        return "materials/extracted";
      }
      if (isDetails && subPage && !fourthPart) {
        return "materials/:materialId";
      }
      return "materials";
    }

    // Handle schedule routes
    if (pageName === "schedule") {
      if (subPage === "project-schedules") {
        return "schedule/project-schedules";
      }
      if (subPage === "project-schedule" && fourthPart) {
        return "project-schedule/:selectedVersionId";
      }
      if (subPage === "change-impact") {
        return "schedule/change-impact";
      }
      if (subPage === "submittal-linking") {
        return "schedule/submittal-linking";
      }
      if (subPage === "material-linking") {
        return "schedule/material-linking";
      }
      if (subPage === "procurement-schedule") {
        return "schedule/procurement-schedule";
      }
      return "schedule";
    }

    // Handle reports sub-routes
    if (pageName === "reports") {
      if (subPage === "custom-report" && fourthPart) {
        return "custom-report/:reportId";
      }
      if (subPage) {
        return `reports/${subPage}`; // risk, upcoming, activities, procurement-follow-ups
      }
      return "reports";
    }

    // Handle settings routes
    if (pageName === "settings") {
      if (subPage && fourthPart && fifthPart) {
        return `${pageName}/${subPage}/${fourthPart}/${fifthPart}`;
      }
      if (subPage && fourthPart) {
        return `${pageName}/${subPage}/${fourthPart}`;
      }
      if (subPage) {
        return `${pageName}/${subPage}`;
      }
      return pageName;
    }

    return pageName;
  }

  // For non-project routes, return the last part
  return parts[parts.length - 1] || "";
}

function useValidateRouteAccess({
  userRole
}: {
  userRole: string | undefined;
}): boolean {
  const { pathname } = useLocation();

  return useMemo(() => {
    // Check if pathname matches any valid route pattern
    const matchesValidRoute = VALID_ROUTE_PATTERNS.some((route) => {
      const match = matchPath(pathname, {
        path: route.path,
        exact: route.exact,
        strict: false
      });
      return match !== null;
    });

    // If route doesn't match any valid pattern, it's invalid
    if (!matchesValidRoute) {
      return false;
    }

    // For project routes, check role permissions
    if (pathname.startsWith("/project/")) {
      // If no user role is set, deny access
      if (!userRole) {
        return false;
      }

      // Extract page key for role checking
      const pageKey = extractPageKey(pathname);
      const allowedRoles = PAGE_ROLE_MAPPING[pageKey];

      // If no role mapping exists for this page, deny access
      if (!allowedRoles) {
        return false;
      }

      // Check if user's role is in the allowed roles list
      return allowedRoles.includes(userRole);
    }

    return true;
  }, [pathname, userRole]);
}

export default useValidateRouteAccess;
